import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRewards, REDEEM_OPTIONS, type RewardsData, type RedeemedCoupon, type RedeemOption } from "@/hooks/useRewards";
import RedeemOptionCard from "@/pages/redeem/components/RedeemOptionCard";
import CouponCard from "@/pages/redeem/components/CouponCard";
import RedeemConfirmModal from "@/pages/redeem/components/RedeemConfirmModal";
import RedeemSuccessModal from "@/pages/redeem/components/RedeemSuccessModal";

export default function RedeemPage() {
  const { user, isLoggedIn } = useAuth();
  const { getRewards, getRedeemedCoupons, redeemPoints } = useRewards();
  const navigate = useNavigate();

  const [rewards, setRewards] = useState<RewardsData | null>(null);
  const [coupons, setCoupons] = useState<RedeemedCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState<RedeemOption | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [newCoupon, setNewCoupon] = useState<RedeemedCoupon | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [activeTab, setActiveTab] = useState<"redeem" | "history">("redeem");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [r, c] = await Promise.all([
      getRewards(user.id),
      getRedeemedCoupons(user.id),
    ]);
    setRewards(r);
    setCoupons(c);
    setLoading(false);
  }, [user, getRewards, getRedeemedCoupons]);

  useEffect(() => {
    if (!isLoggedIn) { navigate("/auth"); return; }
    loadData();
  }, [isLoggedIn, loadData, navigate]);

  const handleSelectOption = (option: RedeemOption) => {
    if (!rewards || rewards.points < option.points) {
      showToast("نقاطك غير كافية لهذا الخيار", "error");
      return;
    }
    setSelectedOption(option);
    setConfirmOpen(true);
  };

  const handleConfirmRedeem = async () => {
    if (!selectedOption || !user) return;
    setRedeeming(true);
    const result = await redeemPoints(user.id, selectedOption);
    setRedeeming(false);
    setConfirmOpen(false);

    if (result.success && result.coupon) {
      setNewCoupon(result.coupon);
      setSuccessOpen(true);
      await loadData();
    } else {
      showToast(result.error ?? "حدث خطأ أثناء الاستبدال", "error");
    }
    setSelectedOption(null);
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      showToast("تم نسخ الكوبون ✓");
      setTimeout(() => setCopiedCode(null), 2500);
    });
  };

  const activeCoupons = coupons.filter((c) => c.status === "active");
  const usedCoupons = coupons.filter((c) => c.status !== "active");

  if (!isLoggedIn) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] px-4 py-16" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] bg-amber-400/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-green-400/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-2xl mx-auto">
        {/* Nav */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/rewards")}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-arrow-right-line"></i>
            المكافآت
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/8 text-gray-400 hover:text-white text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-store-2-line text-sm"></i>
              المتجر
            </button>
          </div>
        </div>

        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-white text-2xl font-black flex items-center gap-3" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-xl">
              <i className="ri-exchange-dollar-line text-amber-400 text-lg"></i>
            </div>
            استبدال النقاط
          </h1>
          <p className="text-gray-500 text-sm mt-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
            حوّل نقاطك إلى كوبونات خصم واستخدمها في طلباتك القادمة
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-500">
            <i className="ri-loader-4-line text-3xl animate-spin text-amber-400"></i>
            <p className="text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>جاري التحميل...</p>
          </div>
        ) : (
          <>
            {/* Points Balance Card */}
            {rewards && (
              <div className="bg-gradient-to-br from-amber-400/15 via-amber-400/5 to-transparent border border-amber-400/25 rounded-3xl p-5 mb-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0"></div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>رصيد نقاطك الحالي</p>
                    <div className="flex items-end gap-2">
                      <span className={`text-4xl font-black ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                        {rewards.points.toLocaleString()}
                      </span>
                      <span className="text-gray-400 text-sm pb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة</span>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 ${rewards.tierBg} border ${rewards.tierBorder} rounded-2xl px-4 py-2.5`}>
                    <i className={`${rewards.tierIcon} ${rewards.tierColor} text-xl`}></i>
                    <div>
                      <p className="text-gray-500 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>مستواك</p>
                      <p className={`${rewards.tierColor} text-sm font-black`} style={{ fontFamily: "'Cairo', sans-serif" }}>{rewards.tierLabel}</p>
                    </div>
                  </div>
                </div>
                {/* Rule */}
                <div className="mt-4 pt-4 border-t border-white/8 flex items-center gap-2">
                  <i className="ri-information-line text-gray-600 text-sm"></i>
                  <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    كل 10 ر.س = نقطة واحدة · الكوبونات صالحة 30 يوماً من تاريخ الإصدار
                  </p>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-[#111] border border-white/8 rounded-2xl p-1 mb-6">
              <button
                onClick={() => setActiveTab("redeem")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "redeem"
                    ? "bg-amber-400 text-black"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-exchange-dollar-line text-base"></i>
                استبدال النقاط
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "history"
                    ? "bg-white/10 text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-coupon-3-line text-base"></i>
                كوبوناتي
                {activeCoupons.length > 0 && (
                  <span className="bg-green-400/20 text-green-400 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                    {activeCoupons.length}
                  </span>
                )}
              </button>
            </div>

            {/* Redeem Tab */}
            {activeTab === "redeem" && (
              <div className="flex flex-col gap-4">
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  اختر خيار الاستبدال المناسب لك:
                </p>
                {REDEEM_OPTIONS.map((option) => (
                  <RedeemOptionCard
                    key={option.points}
                    option={option}
                    currentPoints={rewards?.points ?? 0}
                    onSelect={handleSelectOption}
                  />
                ))}

                {/* How it works */}
                <div className="bg-[#111] border border-white/8 rounded-3xl p-5 mt-2">
                  <p className="text-white text-sm font-black mb-4 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-question-line text-amber-400"></i>
                    كيف يعمل الاستبدال؟
                  </p>
                  <div className="flex flex-col gap-3">
                    {[
                      { icon: "ri-cursor-line", text: "اختر خيار الاستبدال المناسب لنقاطك" },
                      { icon: "ri-coupon-3-line", text: "سيُنشأ كوبون خصم فوري لحسابك" },
                      { icon: "ri-shopping-cart-2-line", text: "استخدم الكوبون عند إتمام أي طلب" },
                      { icon: "ri-time-line", text: "الكوبون صالح لمدة 30 يوماً من الإصدار" },
                    ].map((step, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-xl flex-shrink-0">
                          <i className={`${step.icon} text-amber-400 text-sm`}></i>
                        </div>
                        <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{step.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Coupons History Tab */}
            {activeTab === "history" && (
              <div className="flex flex-col gap-4">
                {coupons.length === 0 ? (
                  <div className="bg-[#111] border border-white/8 rounded-3xl p-10 flex flex-col items-center gap-4 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 border border-amber-400/20 rounded-2xl">
                      <i className="ri-coupon-3-line text-amber-400 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>لا يوجد كوبونات بعد</p>
                      <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        استبدل نقاطك للحصول على كوبونات خصم
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab("redeem")}
                      className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-5 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      <i className="ri-exchange-dollar-line"></i>
                      استبدل الآن
                    </button>
                  </div>
                ) : (
                  <>
                    {activeCoupons.length > 0 && (
                      <div>
                        <p className="text-green-400 text-xs font-bold mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          <i className="ri-checkbox-circle-line"></i>
                          كوبونات نشطة ({activeCoupons.length})
                        </p>
                        <div className="flex flex-col gap-3">
                          {activeCoupons.map((c) => (
                            <CouponCard
                              key={c.id}
                              coupon={c}
                              onCopy={handleCopy}
                              copiedCode={copiedCode}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {usedCoupons.length > 0 && (
                      <div>
                        <p className="text-gray-600 text-xs font-bold mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          <i className="ri-history-line"></i>
                          كوبونات مستخدمة / منتهية ({usedCoupons.length})
                        </p>
                        <div className="flex flex-col gap-3">
                          {usedCoupons.map((c) => (
                            <CouponCard
                              key={c.id}
                              coupon={c}
                              onCopy={handleCopy}
                              copiedCode={copiedCode}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmOpen && selectedOption && rewards && (
        <RedeemConfirmModal
          option={selectedOption}
          currentPoints={rewards.points}
          onConfirm={handleConfirmRedeem}
          onCancel={() => { setConfirmOpen(false); setSelectedOption(null); }}
          loading={redeeming}
        />
      )}

      {/* Success Modal */}
      {successOpen && newCoupon && (
        <RedeemSuccessModal
          coupon={newCoupon}
          onClose={() => { setSuccessOpen(false); setNewCoupon(null); setActiveTab("history"); }}
          onCopy={handleCopy}
          copiedCode={copiedCode}
          onShop={() => navigate("/")}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className={`text-white text-sm px-5 py-3 rounded-xl flex items-center gap-2 ${toast.type === "error" ? "bg-red-500" : "bg-[#1a1a1a]"}`}
            style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className={toast.type === "error" ? "ri-error-warning-line text-red-200" : "ri-checkbox-circle-line text-green-400"}></i>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
